import { useMutation } from '@tanstack/react-query';
import { useMixpanel } from 'react-mixpanel-browser';

import { deviceOtaDetailQueryKey } from '@/api/ota/useDeviceOtaDetail';
import { deviceOtaReadinessQueryKey } from '@/api/ota/useDeviceOtaReadiness';
import { axios } from '@/common/axios';
import { MutationConfig, queryClient } from '@/common/react-query';
import { APIResponse } from '@/common/types';
import { useErrorContext } from '@/context/ErrorContext';

export type ExecuteDeviceOtaPayload = { spotterId: string };

/**
 * The started plan is read back through `useDeviceOtaDetail`, which `onSuccess` below invalidates, so
 * this response body is left unmodeled.
 */
export type ExecuteDeviceOtaResponse = APIResponse<unknown>;

export const executeDeviceOta = ({ spotterId }: ExecuteDeviceOtaPayload): Promise<ExecuteDeviceOtaResponse> => {
    return axios.post(`/user-rest/devices/${spotterId}/ota`);
};

type UseExecuteDeviceOtaOptions = {
    config?: MutationConfig<typeof executeDeviceOta>;
    data?: ExecuteDeviceOtaPayload;
};

export const useExecuteDeviceOta = ({ config }: UseExecuteDeviceOtaOptions = {}) => {
    const { createErrorHandler } = useErrorContext();
    const mixpanel = useMixpanel();
    return useMutation({
        ...config,
        mutationFn: executeDeviceOta,
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: deviceOtaDetailQueryKey(variables.spotterId) });
            queryClient.invalidateQueries({ queryKey: deviceOtaReadinessQueryKey(variables.spotterId) });
            mixpanel?.track('OTA update started', {
                spotterId: variables.spotterId,
            });
        },
        onError: createErrorHandler('start OTA update'),
    });
};
